import type { ToastOptions, ToastType } from '@/components/feedback/toast-store'
import type { BadgeTone } from '@/components/ui/badge'
import { DISPLAY_TIME_ZONE } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { HrAction, HrHistoryEvent, HrProcess } from '@/services/http/hr'

/**
 * İK paneli saf yardımcıları: durum tonları, geçmişin gün gruplaması, saat dönüşümleri.
 * Durum adları Cube (`cube/model/passgage/*`) ve backend (`modules/hr/catalog.py`) ile aynıdır.
 */

const RECORD_TONES: Record<string, BadgeTone> = {
  aktarildi: 'success',
  elle_girildi: 'success',
  hatali: 'danger',
  bekliyor: 'warning',
  silindi: 'neutral',
  yoksayildi: 'neutral',
  atlandi: 'neutral',
}

export function recordStatusTone(status: string | null | undefined): BadgeTone {
  return (status && RECORD_TONES[status]) || 'neutral'
}

const ACTION_TONES: Record<string, BadgeTone> = {
  bekliyor: 'info',
  calisiyor: 'info',
  istendi: 'info',
  tamam: 'success',
  hata: 'danger',
  tutarsiz: 'warning',
  iptal: 'neutral',
  bilgi: 'neutral',
}

export function actionStatusTone(status: string | null | undefined): BadgeTone {
  return (status && ACTION_TONES[status]) || 'neutral'
}

export const OPEN_ACTION_STATUSES = new Set(['bekliyor', 'calisiyor'])

export function isOpenAction(action: Pick<HrAction, 'status'> | undefined): boolean {
  return !!action && OPEN_ACTION_STATUSES.has(action.status)
}

const ACTION_TOAST_TYPES: Record<string, ToastType> = {
  tamam: 'success',
  hata: 'error',
  tutarsiz: 'warning',
}

/**
 * Aksiyonun izleme bildirimi: açıkken dönen simge (sırada → devam ediyor, akışın son adım
 * mesajıyla), kapanınca sonucu. Aynı aksiyon aynı kartı yerinde günceller (`id`).
 */
export function actionToast(action: HrAction, label: string): ToastOptions {
  const text = messages.hr.actions.progress
  const lastEvent = action.events?.at(-1)
  const id = `hr-action-${action.id}`
  if (action.status === 'bekliyor') {
    return { id, type: 'loading', title: text.queued(label), description: text.queuedHint }
  }
  if (action.status === 'calisiyor') {
    return {
      id,
      type: 'loading',
      title: text.running(label),
      description: lastEvent?.message ?? text.runningHint,
    }
  }
  return {
    id,
    type: ACTION_TOAST_TYPES[action.status] ?? 'info',
    title: text.done[action.status]?.(label) ?? label,
    description: action.message ?? lastEvent?.message ?? undefined,
  }
}

/** Pasife alma DİA'da yapıldı ama Passgage adımı kaldı (Q2): satırda "Tutarsız" + "Tamamla". */
export function isInconsistentDeactivation(action: HrAction | undefined): boolean {
  return (
    !!action &&
    action.status === 'tutarsiz' &&
    (action.type === 'personel_pasife_al' || action.type === 'personel_pasife_al_tamamla')
  )
}

export type ProcessHealth = 'failed' | 'overdue' | 'ok' | 'unknown'

/** Süreç kartının uyarı rozeti: son çalışma başarısız ya da planlı zamanı geçmiş. */
export function processHealth(process: HrProcess): ProcessHealth {
  if (!process.found) return 'unknown'
  if (process.last_failed) return 'failed'
  if (process.overdue) return 'overdue'
  return process.last_run ? 'ok' : 'unknown'
}

const dayKeyFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Offset'li ISO zaman -> İstanbul takvim günü `YYYY-MM-DD`. */
export function istanbulDay(iso: string): string {
  return dayKeyFormatter.format(new Date(iso))
}

/** Geçmiş olaylarını İstanbul gününe göre gruplar; sıra (en yeni üstte) korunur. */
export function groupByDay<T extends Pick<HrHistoryEvent, 'at'>>(
  events: T[],
): { day: string; events: T[] }[] {
  const groups: { day: string; events: T[] }[] = []
  for (const event of events) {
    const day = istanbulDay(event.at)
    const last = groups[groups.length - 1]
    if (last && last.day === day) last.events.push(event)
    else groups.push({ day, events: [event] })
  }
  return groups
}

/**
 * Formdaki İstanbul tarihi + saati -> offset'li ISO. Türkiye 2016'dan beri yaz saati
 * uygulamıyor, sabit UTC+3.
 */
export function istanbulToIso(date: string, time: string): string {
  return `${date}T${time}:00+03:00`
}

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/** Offset'li ISO -> İstanbul `{ date: YYYY-MM-DD, time: HH:MM }` (form varsayılanları). */
export function isoToIstanbul(iso: string): { date: string; time: string } {
  const parts = Object.fromEntries(
    partsFormatter.formatToParts(new Date(iso)).map((part) => [part.type, part.value]),
  )
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  }
}

/** Cube'un offset'siz İstanbul duvar saati -> `{ date, time }` (metinden, `new Date` yok). */
export function cubeWallClock(
  value: string | null | undefined,
): { date: string; time: string } | null {
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(value ?? '')
  return match ? { date: match[1], time: match[2] } : null
}

/** "HH:MM" geçerli mi (00:00-23:59). */
export function isTimeOfDay(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
}

/**
 * Elle yazılan saati "HH:MM"e çevirir: "8:00", "8.00", "800", "0800" kabul edilir. Tanınmazsa
 * değeri olduğu gibi döner (doğrulama `isTimeOfDay` ile).
 */
export function normalizeTime(value: string): string {
  const match = /^(\d{1,2})[:.]?(\d{2})$/.exec(value.trim())
  if (!match) return value.trim()
  const normalized = `${match[1].padStart(2, '0')}:${match[2]}`
  return isTimeOfDay(normalized) ? normalized : value.trim()
}

/** Offset'li ISO andan bu yana geçen süre, kaba birimle: "12 dk", "5 sa", "3 gün". */
export function waitingFor(iso: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000))
  if (minutes < 60) return `${minutes} dk`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} sa`
  return `${Math.floor(hours / 24)} gün`
}
